import React, { useState, useEffect, useCallback } from 'react';
import { Users, Loader2 } from 'lucide-react';
import { apiUrl } from '../config/api';

interface UserAccountProps {
  user: {
    id: string;
    email: string;
    fullName: string;
    firstName?: string;
    lastName?: string;
    company: string;
    role: string;
  };
  onLogout: () => void;
}

interface TeamMember {
  id: string;
  email: string;
  fullName: string;
  company: string;
  role: string;
  relationship: 'inviter' | 'sibling' | 'invitee';
  joinedAt: string;
  invitedBy: string;
}

export function UserAccount({ user, onLogout }: UserAccountProps) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Extract firstName and lastName from fullName if not already set
  const getInitialFirstName = () => {
    if (user.firstName) return user.firstName;
    if (user.fullName) {
      const parts = user.fullName.trim().split(/\s+/);
      return parts[0] || '';
    }
    return '';
  };

  const getInitialLastName = () => {
    if (user.lastName) return user.lastName;
    if (user.fullName) {
      const parts = user.fullName.trim().split(/\s+/);
      return parts.slice(1).join(' ') || '';
    }
    return '';
  };

  const [profile, setProfile] = useState({
    fullName: user.fullName || '',
    firstName: getInitialFirstName(),
    lastName: getInitialLastName(),
    company: user.company || ''
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  useEffect(() => {
    const firstName = user.firstName || (user.fullName ? user.fullName.trim().split(/\s+/)[0] : '');
    const lastName = user.lastName || (user.fullName ? user.fullName.trim().split(/\s+/).slice(1).join(' ') : '');
    setProfile({
      fullName: user.fullName || '',
      firstName,
      lastName,
      company: user.company || ''
    });
  }, [user.fullName, user.firstName, user.lastName, user.company]);

  const fetchProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setProfileError('Authentication required');
        setProfileLoading(false);
        return;
      }

      const response = await fetch(apiUrl(`/api/auth/profile?_t=${Date.now()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });

      const data = await response.json();
      if (data.success) {
        setProfile({
          fullName: data.data.fullName || '',
          firstName: data.data.firstName || '',
          lastName: data.data.lastName || '',
          company: data.data.company || ''
        });
        setProfileError(null);
      } else {
        setProfileError(data.message || 'Failed to load profile');
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setProfileError('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const fetchTeamMembers = useCallback(async () => {
    try {
      setTeamLoading(true);
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setError('Authentication required');
        setTeamLoading(false);
        return;
      }
      
      const response = await fetch(apiUrl(`/api/team-members?_t=${Date.now()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      const data = await response.json();
      console.log('🔍 UserAccount - Team members API response:', data);
      if (data.success) {
        console.log('🔍 UserAccount - Team members data:', data.data.teamMembers);
        setTeamMembers(data.data.teamMembers);
      } else {
        console.error('🔍 UserAccount - API error:', data.message);
        setError(data.message || 'Failed to fetch team members');
      }
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError('Failed to fetch team members');
    } finally {
      setTeamLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchTeamMembers();
  }, [fetchProfile, fetchTeamMembers]);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileSuccess(null);
    setProfileError(null);

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setProfileError('Authentication required');
        setProfileSaving(false);
        return;
      }

      const response = await fetch(apiUrl('/api/auth/profile'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // Reconstruct fullName from firstName and lastName
          fullName: [profile.firstName.trim(), profile.lastName.trim()].filter(Boolean).join(' ').trim() || profile.fullName.trim(),
          firstName: profile.firstName.trim(),
          lastName: profile.lastName.trim(),
          company: profile.company.trim()
        })
      });

      const data = await response.json();
      if (data.success) {
        const updatedProfile = {
          fullName: data.data.fullName || '',
          firstName: data.data.firstName || '',
          lastName: data.data.lastName || '',
          company: data.data.company || ''
        };
        setProfile(updatedProfile);
        setProfileSuccess('Profile updated successfully');
        window.dispatchEvent(new CustomEvent('userProfileUpdated', { detail: updatedProfile }));
      } else {
        setProfileError(data.message || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileError('Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordError(null);
    setPasswordSuccess(null);

    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all password fields');
      setPasswordSaving(false);
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      setPasswordSaving(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordSaving(false);
      return;
    }

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      if (!token) {
        setPasswordError('Authentication required');
        setPasswordSaving(false);
        return;
      }

      const response = await fetch(apiUrl('/api/auth/change-password'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: currentPassword,
          newPassword: newPassword
        })
      });

      const data = await response.json();
      if (data.success) {
        setPasswordSuccess('Password updated successfully');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setPasswordError(data.message || 'Failed to update password');
      }
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordError('Failed to update password');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 600,
          color: 'var(--dark)',
          fontFamily: 'var(--montserrat)',
          marginBottom: '0.5rem'
        }}>
          My Account
        </h1>
        <p style={{
          color: 'var(--gray)',
          fontSize: '1.1rem'
        }}>
          Manage your profile and connect with your team
        </p>
      </div>

      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          color: '#991b1b'
        }}>
          <span>{error}</span>
        </div>
      )}

      <div style={{ display: 'grid', gap: '2rem' }}>
        {/* Basic Info Section */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #eee',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem',
            borderBottom: '1px solid #eee',
            background: '#f8f9fa'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--dark)',
              fontFamily: 'var(--montserrat)',
              margin: 0
            }}>
              Basic Information
            </h2>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {profileError && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                color: '#991b1b'
              }}>
                {profileError}
              </div>
            )}
            {profileSuccess && (
              <div style={{
                background: '#dcfce7',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                color: '#166534'
              }}>
                {profileSuccess}
              </div>
            )}
            {profileLoading ? (
              <div style={{ padding: '1rem', color: 'var(--gray)' }}>Loading profile...</div>
            ) : (
              <form onSubmit={handleProfileSave} style={{ display: 'grid', gap: '1.25rem', maxWidth: '600px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={profile.firstName}
                    onChange={(e) => setProfile(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Enter your first name"
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontFamily: 'var(--montserrat)'
                    }}
                    disabled={profileSaving}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={profile.lastName}
                    onChange={(e) => setProfile(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Enter your last name"
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontFamily: 'var(--montserrat)'
                    }}
                    disabled={profileSaving}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      background: '#f8f9fa',
                      color: 'var(--dark)',
                      fontFamily: 'var(--montserrat)'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                    Company
                  </label>
                  <input
                    type="text"
                    value={profile.company}
                    onChange={(e) => setProfile(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Enter your company name"
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontFamily: 'var(--montserrat)'
                    }}
                    disabled={profileSaving}
                  />
                </div>
                <button
                  type="submit"
                  disabled={profileSaving}
                  style={{
                    background: profileSaving ? '#ccc' : 'var(--orange)',
                    color: profileSaving ? '#666' : 'var(--dark)',
                    border: 'none',
                    padding: '0.85rem 1.75rem',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontFamily: 'var(--montserrat)',
                    cursor: profileSaving ? 'not-allowed' : 'pointer',
                    justifySelf: 'flex-start',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!profileSaving) {
                      e.currentTarget.style.background = '#c4a020';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = profileSaving ? '#ccc' : 'var(--orange)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {profileSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Password Section */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #eee',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem',
            borderBottom: '1px solid #eee',
            background: '#f8f9fa'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--dark)',
              fontFamily: 'var(--montserrat)',
              margin: 0
            }}>
              Password & Security
            </h2>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {passwordError && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                color: '#991b1b'
              }}>
                {passwordError}
              </div>
            )}
            {passwordSuccess && (
              <div style={{
                background: '#dcfce7',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                color: '#166534'
              }}>
                {passwordSuccess}
              </div>
            )}
            <form onSubmit={handlePasswordChange} style={{ display: 'grid', gap: '1.25rem', maxWidth: '600px' }}>
              <input
                type="email"
                value={user.email}
                readOnly
                hidden
                autoComplete="username"
                aria-hidden="true"
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="Enter your current password"
                  autoComplete="current-password"
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontFamily: 'var(--montserrat)'
                  }}
                  disabled={passwordSaving}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter a new password"
                  autoComplete="new-password"
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontFamily: 'var(--montserrat)'
                  }}
                  disabled={passwordSaving}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--gray)' }}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm your new password"
                  autoComplete="new-password"
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontFamily: 'var(--montserrat)'
                  }}
                  disabled={passwordSaving}
                />
              </div>
              <button
                type="submit"
                disabled={passwordSaving}
                style={{
                  background: passwordSaving ? '#ccc' : 'var(--dark)',
                  color: 'white',
                  border: 'none',
                  padding: '0.85rem 1.75rem',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontFamily: 'var(--montserrat)',
                  cursor: passwordSaving ? 'not-allowed' : 'pointer',
                  justifySelf: 'flex-start',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  if (!passwordSaving) {
                    e.currentTarget.style.background = '#010206';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = passwordSaving ? '#ccc' : 'var(--dark)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {passwordSaving ? 'Updating...' : 'Update Password'}
              </button>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--gray)' }}>
                Password must be at least 8 characters long. We recommend using a mix of letters, numbers, and symbols.
              </p>
            </form>
          </div>
        </div>

        {/* Team Collaboration Section */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #eee',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem',
            borderBottom: '1px solid #eee',
            background: '#f8f9fa'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'var(--dark)',
              fontFamily: 'var(--montserrat)',
              margin: 0
            }}>
              Team Collaboration
            </h2>
          </div>
          <div style={{ padding: '1.5rem' }}>
                  {teamLoading ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <Loader2 style={{
                  width: '1.5rem',
                  height: '1.5rem',
                  color: 'var(--orange)',
                  animation: 'spin 1s linear infinite'
                }} />
                <p style={{ margin: 0, color: 'var(--gray)', fontSize: '0.9rem' }}>
                  Loading team members...
                </p>
              </div>
            ) : teamMembers.length === 0 ? (
              <div style={{
                padding: '2rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #ddd',
                textAlign: 'center',
                color: 'var(--gray)'
              }}>
                <Users style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.5 }} />
                <p style={{ margin: 0, fontSize: '1rem', fontWeight: 500 }}>
                  No team members found
                </p>
                <p style={{ margin: '0.5rem 0 1.5rem', fontSize: '0.9rem' }}>
                  Invite colleagues to start collaborating!
                </p>
                <button
                  onClick={() => {
                    const event = new CustomEvent('showInterestForm', {
                      detail: { formType: 'team-invitation' }
                    });
                    window.dispatchEvent(event);
                  }}
                  style={{
                    background: 'var(--orange)',
                    color: 'var(--dark)',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    margin: '0 auto',
                    transition: 'all 0.2s ease',
                    fontFamily: 'var(--montserrat)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#c4a020';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'var(--orange)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Users style={{ width: '1rem', height: '1rem' }} />
                  Invite Colleagues
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {teamMembers.map((member, index) => (
                  <div
                    key={member.id || index}
                    style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '1rem',
                      border: '1px solid #ddd',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{
                        fontWeight: 600,
                        color: 'var(--dark)',
                        fontSize: '1rem',
                        fontFamily: 'var(--montserrat)'
                      }}>
                        {member.fullName || 'Unknown User'}
                      </div>
                      <div style={{
                        color: 'var(--gray)',
                        fontSize: '0.9rem'
                      }}>
                        {member.email}
                      </div>
                      {member.company && (
                        <div style={{
                          color: 'var(--gray)',
                          fontSize: '0.9rem'
                        }}>
                          {member.company} • {member.role}
                        </div>
                      )}
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: '0.25rem'
                    }}>
                      <span style={{
                        fontSize: '0.8rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        background: member.relationship === 'inviter' ? '#e3f2fd' : 
                                   member.relationship === 'sibling' ? '#f3e5f5' : '#e8f5e8',
                        color: member.relationship === 'inviter' ? '#1976d2' :
                               member.relationship === 'sibling' ? '#7b1fa2' : '#388e3c',
                        fontWeight: 500
                      }}>
                        {member.relationship === 'inviter' ? 'Invited you' :
                         member.relationship === 'sibling' ? 'Colleague' : 'You invited'}
                      </span>
                      <span style={{
                        fontSize: '0.75rem',
                        color: 'var(--gray)'
                      }}>
                        Joined {new Date(member.joinedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
